import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { settingsApi } from '../services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Building2, Calendar as CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';

const DATE_PERIODS = [
  { value: 'all', label: 'All Time' },
  { value: 'this_year', label: 'This Fiscal Year' },
  { value: 'last_year', label: 'Last Fiscal Year' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' },
];

export const GlobalFilters = ({ 
  onBuChange, 
  onPeriodChange,
  onCustomDateChange,
  selectedBu = '',
  selectedPeriod = 'all',
  showBuFilter = true,
  showPeriodFilter = true,
  showFiscalYearFilter = false,
  fiscalYears = []
}) => {
  const [businessUnits, setBusinessUnits] = useState([]);
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const { isAdmin, getUserBuId } = useAuth();
  const { isDark, setSelectedBu } = useTheme();

  useEffect(() => {
    if (isAdmin() && showBuFilter) {
      settingsApi.getBusinessUnits()
        .then(res => setBusinessUnits(res.data))
        .catch(() => {});
    }
  }, [isAdmin, showBuFilter]);

  const handleBuChange = (value) => {
    const bu = businessUnits.find(b => b.id === value);
    if (setSelectedBu) {
      setSelectedBu(value === 'all' ? '' : value, bu?.logo_url);
    }
    if (onBuChange) {
      onBuChange(value === 'all' ? '' : value);
    }
  };

  const handlePeriodChange = (value) => {
    if (value === 'custom') {
      setShowCustomPicker(true);
    } else {
      setShowCustomPicker(false);
      setCustomDateRange({ from: null, to: null });
    }
    if (onPeriodChange) {
      onPeriodChange(value === 'all' ? '' : value);
    }
  };

  const handleCustomDateSelect = (range) => {
    setCustomDateRange(range);
    if (range?.from && range?.to && onCustomDateChange) {
      onCustomDateChange(range);
    }
  };

  const clearFilters = () => {
    if (onBuChange) onBuChange('');
    if (onPeriodChange) onPeriodChange('');
    setCustomDateRange({ from: null, to: null });
    setShowCustomPicker(false);
  };

  // Non-admins don't see BU filter (they only see their own BU data)
  const showBu = showBuFilter && isAdmin();

  // Determine current period value for select
  const currentPeriodValue = selectedPeriod || 'all';

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Business Unit Filter - Admin only */}
      {showBu && businessUnits.length > 0 && (
        <div className="flex items-center gap-2">
          <Building2 className={`w-4 h-4 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`} strokeWidth={1.5} />
          <Select value={selectedBu || 'all'} onValueChange={handleBuChange}>
            <SelectTrigger 
              className={`w-[200px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
              data-testid="bu-filter"
            >
              <SelectValue placeholder="All Business Units" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Business Units</SelectItem>
              {businessUnits.map(bu => (
                <SelectItem key={bu.id} value={bu.id}>
                  <div className="flex items-center gap-2">
                    {bu.logo_url && (
                      <img src={bu.logo_url} alt="" className="w-4 h-4 object-contain" />
                    )}
                    {bu.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Date Period Filter */}
      {showPeriodFilter && (
        <div className="flex items-center gap-2">
          <CalendarIcon className={`w-4 h-4 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`} strokeWidth={1.5} />
          <Select value={currentPeriodValue} onValueChange={handlePeriodChange}>
            <SelectTrigger 
              className={`w-[180px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
              data-testid="period-filter"
            >
              <SelectValue placeholder="All Time" />
            </SelectTrigger>
            <SelectContent>
              {DATE_PERIODS.map(period => (
                <SelectItem key={period.value} value={period.value}>
                  {period.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Custom Date Range Picker */}
      {showCustomPicker && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={`rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
              data-testid="custom-date-picker"
            >
              <CalendarIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {customDateRange.from ? (
                customDateRange.to ? (
                  <>
                    {format(customDateRange.from, 'MMM d, yyyy')} - {format(customDateRange.to, 'MMM d, yyyy')}
                  </>
                ) : (
                  format(customDateRange.from, 'MMM d, yyyy')
                )
              ) : (
                'Select dates'
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={customDateRange}
              onSelect={handleCustomDateSelect}
              numberOfMonths={2}
              data-testid="date-range-calendar"
            />
          </PopoverContent>
        </Popover>
      )}

      {/* Clear Filters Button */}
      {(selectedBu || (selectedPeriod && selectedPeriod !== 'all')) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearFilters}
          className={`rounded-sm ${isDark ? 'text-zinc-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
          data-testid="clear-filters"
        >
          <X className="w-4 h-4 mr-1" />
          Clear
        </Button>
      )}
    </div>
  );
};

export default GlobalFilters;
